import Vue from "vue";
import { API_ROUTES } from "@/utils/API";
import { SMCases } from "@/store";

const DEFAULT_BASE_URL = "https://chatwoot.nationbetter.uk";
const DEFAULT_WEBSITE_TOKEN = "8TpFUPApx7SrBJ1G4niZ6gdf";

declare global {
  interface Window {
    chatwootSDK?: { run: (config: { websiteToken: string; baseUrl: string }) => void };
    $chatwoot?: {
      reset?: () => void;
      setUser?: (
        identifier: string,
        user: {
          email?: string;
          name?: string;
          identifier_hash?: string;
          company_name?: string;
        }
      ) => void;
      setCustomAttributes?: (attributes: Record<string, string | number | boolean | null>) => void;
      toggle?: (state?: "open" | "close") => void;
      toggleBubbleVisibility?: (state: "show" | "hide") => void;
    };
  }
}

type ChatwootUser = {
  id: string;
  email?: string;
  name?: string;
  company_name?: string;
  chatwoot_identifier?: string;
  customAttributes?: Record<string, string | number | boolean | null>;
};

type ChatwootConfig = {
  baseUrl?: string;
  websiteToken?: string;
  user?: ChatwootUser;
};

const SDK_SCRIPT_ID = "chatwoot-sdk";
const POSITION_STYLE_ID = "chatwoot-position-override";
const CHATWOOT_USER_RETRY_DELAY_MS = 200;
const CHATWOOT_USER_MAX_RETRIES = 10;
const CHATWOOT_READY_WAIT_TIMEOUT_MS = 5000;
const CHATWOOT_VISIBILITY_RETRY_DELAY_MS = 250;
const CHATWOOT_VISIBILITY_MAX_RETRIES = 8;

const CHATWOOT_ERROR = "chatwoot:error";
const CHATWOOT_ON_MESSAGE = "chatwoot:on-message";
const CHATWOOT_ON_START_CONVERSATION = "chatwoot:on-start-conversation";
const CHATWOOT_POSTBACK = "chatwoot:postback";
const CHATWOOT_READY = "chatwoot:ready";
const CHATWOOT_OPENED = "chatwoot:opened";
const CHATWOOT_CLOSED = "chatwoot:closed";

const CHATWOOT_SDK_WINDOW_EVENTS: ReadonlyArray<readonly [string, string]> = [
  ["CHATWOOT_ON_MESSAGE", CHATWOOT_ON_MESSAGE],
  ["CHATWOOT_ON_START_CONVERSATION", CHATWOOT_ON_START_CONVERSATION],
  ["CHATWOOT_POSTBACK", CHATWOOT_POSTBACK],
  ["CHATWOOT_READY", CHATWOOT_READY],
  ["CHATWOOT_OPENED", CHATWOOT_OPENED],
  ["CHATWOOT_CLOSED", CHATWOOT_CLOSED],
  ["CHATWOOT_ERROR", CHATWOOT_ERROR],
];

let chatwootSenderLogListenersAttached = false;
let chatwootReadyUserSyncAttached = false;
let chatwootReadyFired = false;
let lastChatwootConversationSyncAt = 0;
const CHATWOOT_CONVERSATION_SYNC_THROTTLE_MS = 10_000;
let pendingChatwootUser: ChatwootUser | null = null;

type ChatwootConversationSyncPayload = {
  case_id: number | string;
  conversation_id: number | string;
};

function extractChatwootConversationSyncPayload(
  detail: unknown
): ChatwootConversationSyncPayload | null {
  if (!detail || typeof detail !== "object") return null;
  const d = detail as Record<string, unknown>;

  const additionalAttributes = d.additional_attributes as unknown;

  const isAdditionalAttributesEmpty =
    additionalAttributes != null &&
    typeof additionalAttributes === "object" &&
    !Array.isArray(additionalAttributes) &&
    Object.keys(additionalAttributes as Record<string, unknown>).length === 0;

  if (!isAdditionalAttributesEmpty) return null;

  const caseId = (SMCases as any)?.caseId ?? 0;

  const conversationId = d.conversation_id;

  if (conversationId == null) return null;

  return {
    case_id: caseId as number | string,
    conversation_id: conversationId as number | string,
  };
}

async function postChatwootConversationSync(payload: ChatwootConversationSyncPayload) {
  try {
    await Vue.axios.post(API_ROUTES.CHATWOOT_CONVERSATION, payload);
  } catch (error) {
    console.error("[Chatwoot][on-message] Failed to sync conversation", error);
  }
}

function attachChatwootLogging() {
  if (chatwootSenderLogListenersAttached || typeof window === "undefined") return;
  chatwootSenderLogListenersAttached = true;

  for (const [constantName, eventType] of CHATWOOT_SDK_WINDOW_EVENTS) {
    window.addEventListener(eventType, ((event: CustomEvent) => {
      console.log(constantName, eventType, event, event.detail);

      if (eventType === CHATWOOT_ON_MESSAGE) {
        const syncPayload = extractChatwootConversationSyncPayload(event.detail);
        if (syncPayload) {
          const now = Date.now();
          if (now - lastChatwootConversationSyncAt < CHATWOOT_CONVERSATION_SYNC_THROTTLE_MS) {
            return;
          }
          lastChatwootConversationSyncAt = now;
          void postChatwootConversationSync(syncPayload);
        }
      }
    }) as EventListener);
  }
}

function applyLauncherPosition() {
  if (typeof document === "undefined") return;

  if (document.getElementById(POSITION_STYLE_ID)) return;

  const style = document.createElement("style");
  style.id = POSITION_STYLE_ID;
  style.textContent = `
    /* Chatwoot launcher position override */
    .woot-widget-bubble {
      right: 15px !important;
      bottom: 85px !important;
    }
    .woot-widget-holder,
    #chatwoot_live_chat_widget,
    iframe#chatwoot_live_chat_widget {
      right: 15px !important;
      bottom: 164px !important;
    }
  `;

  document.head.appendChild(style);
}

function getScriptEl(): HTMLScriptElement | null {
  return document.getElementById(SDK_SCRIPT_ID) as HTMLScriptElement | null;
}

function chatwootUserIdentifier(userId: string): string {
  const env = (process.env.NODE_ENV as string) ?? "";

  return `${userId}${env}`;
}

function identifyUser(user: ChatwootUser): Promise<boolean> {
  if (!user?.id) return Promise.resolve(false);

  return new Promise((resolve) => {
    const handler = (retriesLeft: number = CHATWOOT_USER_MAX_RETRIES) => {
      try {
        if (!window.$chatwoot?.setUser) {
          if (retriesLeft <= 0) {
            console.error("[Chatwoot][init] setUser unavailable after retries");
            resolve(false);
            return;
          }

          setTimeout(() => handler(retriesLeft - 1), CHATWOOT_USER_RETRY_DELAY_MS);
          return;
        }

        const setUserPayload = {
          email: user.email,
          name: user.name,
          identifier_hash: user.chatwoot_identifier,
          ...(user.company_name ? { company_name: user.company_name } : {}),
        };

        // console.log("[Chatwoot][init] setUser payload", {
        //   ...setUserPayload,
        // });

        window.$chatwoot?.setUser?.(chatwootUserIdentifier(user.id), setUserPayload);

        if (user.customAttributes) {
          // console.log("[Chatwoot][init] setCustomAttributes payload", user.customAttributes);
          window.$chatwoot?.setCustomAttributes?.(user.customAttributes);
        }

        resolve(true);
      } catch (error) {
        console.error("Error setting Chatwoot user", error);
        resolve(false);
      }
    };

    handler();
  });
}

function syncPendingChatwootUser() {
  if (!pendingChatwootUser) return Promise.resolve(false);

  return identifyUser(pendingChatwootUser);
}

function waitForChatwootReady(
  timeoutMs: number = CHATWOOT_READY_WAIT_TIMEOUT_MS
): Promise<boolean> {
  if (typeof window === "undefined") return Promise.resolve(false);
  if (chatwootReadyFired) return Promise.resolve(true);

  return new Promise((resolve) => {
    let resolved = false;
    let timeoutId = 0;
    const onReady = () => {
      if (resolved) return;
      resolved = true;
      chatwootReadyFired = true;
      clearTimeout(timeoutId);
      resolve(true);
    };
    timeoutId = window.setTimeout(() => {
      if (resolved) return;
      resolved = true;
      window.removeEventListener(CHATWOOT_READY, onReady as EventListener);
      resolve(false);
    }, timeoutMs);

    window.addEventListener(CHATWOOT_READY, onReady as EventListener, { once: true });
  });
}

async function ensurePendingChatwootUserSynced(): Promise<boolean> {
  if (!pendingChatwootUser) return false;

  const isReady = await waitForChatwootReady();
  if (!isReady) {
    console.error("[Chatwoot][init] chatwoot:ready was not fired before setUser");
    return false;
  }

  const identified = await syncPendingChatwootUser();
  if (!identified) {
    console.error("[Chatwoot][init] setUser was not confirmed before continuing");
  }

  return identified;
}

function attachChatwootReadyUserSync() {
  if (chatwootReadyUserSyncAttached || typeof window === "undefined") return;
  chatwootReadyUserSyncAttached = true;

  window.addEventListener(CHATWOOT_READY, () => {
    chatwootReadyFired = true;
    void ensurePendingChatwootUserSynced();
  });
}

export function chatwootLoad(config: ChatwootConfig = {}): Promise<boolean> {
  const baseUrl = config.baseUrl ?? DEFAULT_BASE_URL;
  const websiteToken = config.websiteToken ?? DEFAULT_WEBSITE_TOKEN;
  const user = config.user;

  if (typeof window === "undefined") return Promise.resolve(false);
  applyLauncherPosition();
  attachChatwootLogging();
  attachChatwootReadyUserSync();

  if (user) {
    pendingChatwootUser = user;
  }

  if (window.chatwootSDK?.run) {
    return new Promise((resolve) => {
      try {
        window.chatwootSDK.run({ websiteToken, baseUrl });
      } catch (error) {
        console.error("Error loading Chatwoot SDK", error);
      }

      if (user) {
        void ensurePendingChatwootUserSynced().then((isUserSynced) => resolve(isUserSynced));
      } else {
        resolve(false);
      }
    });
  }

  const existing = getScriptEl();
  if (existing) {
    return new Promise((resolve) => {
      existing.addEventListener(
        "load",
        () => {
          window.chatwootSDK?.run?.({ websiteToken, baseUrl });
          if (user) {
            void ensurePendingChatwootUserSynced().then((isUserSynced) => resolve(isUserSynced));
          } else {
            resolve(false);
          }
        },
        { once: true }
      );
      existing.addEventListener("error", () => resolve(false), { once: true });
    });
  }

  return new Promise((resolve) => {
    const g = document.createElement("script");
    g.id = SDK_SCRIPT_ID;
    g.src = `${baseUrl}/packs/js/sdk.js`;
    g.async = true;
    g.onload = () => {
      window.chatwootSDK?.run?.({ websiteToken, baseUrl });
      if (user) {
        void ensurePendingChatwootUserSynced().then((isUserSynced) => resolve(isUserSynced));
      } else {
        resolve(false);
      }
    };
    g.onerror = () => resolve(false);

    const firstScript = document.getElementsByTagName("script")[0];
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(g, firstScript);
    } else {
      document.head.appendChild(g);
    }
  });
}

export function chatwootHide() {
  try {
    window.$chatwoot?.toggle?.("close");
    window.$chatwoot?.toggleBubbleVisibility?.("hide");
  } catch (error) {
    console.error("Error hiding Chatwoot", error);
  }
}

function toggleBubbleVisibilityWithRetry(
  state: "show" | "hide",
  retriesLeft: number = CHATWOOT_VISIBILITY_MAX_RETRIES
) {
  try {
    if (!window.$chatwoot?.toggleBubbleVisibility) {
      if (retriesLeft <= 0) return;

      window.addEventListener(
        "chatwoot:ready",
        () => {
          toggleBubbleVisibilityWithRetry(state, retriesLeft - 1);
        },
        { once: true } as any
      );
      return;
    }

    window.$chatwoot.toggleBubbleVisibility(state);
    return;
  } catch (error) {
    if (retriesLeft <= 0) {
      console.error(`Error toggling Chatwoot bubble: ${state}`, error);
      return;
    }

    setTimeout(
      () => toggleBubbleVisibilityWithRetry(state, retriesLeft - 1),
      CHATWOOT_VISIBILITY_RETRY_DELAY_MS
    );
  }
}

export function chatwootShow() {
  try {
    applyLauncherPosition();
    toggleBubbleVisibilityWithRetry("show");
  } catch (error) {
    console.error("Error showing Chatwoot", error);
  }
}

export function chatwootShutdown() {
  try {
    chatwootHide();
    window.$chatwoot?.reset?.();
  } catch (error) {
    console.error("Error shutting down Chatwoot", error);
  }
}
